// src/api/kanban.js
// Isolated data hooks for the Deal Flow Kanban board (v2).
// Talks only to the new /kanban backend module — it does NOT touch the existing
// startup-listing endpoints used by the production table page.
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { RetryOnError, verifyToken } from "../utils/VerifyToken";
import { checkForAuthorization } from "utils/index";

// Append only the params that are actually present (search + filters).
const buildParams = (obj = {}) => {
    const params = new URLSearchParams();
    Object.entries(obj).forEach(([key, val]) => {
        if (val === undefined || val === null || val === "") return;
        params.append(key, val);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : "";
};

// GET /kanban/board/:investor_id?investor_pipeline_id=&limit=&name=&category=&sub_sector=&select_stages=&city=
// -> { data: { columns: [{ _id, label, value, display_order, count, cards: [...] }] } }
export const FetchKanbanBoard = () => {
    return useMutation(
        ({ investor_id, investor_pipeline_id, limit, name, category, sub_sector, select_stages, city }) => {
            const qs = buildParams({ investor_pipeline_id, limit, name, category, sub_sector, select_stages, city });
            return axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/board/${investor_id}${qs}`,
                headers: { Authorization: `Bearer ${checkForAuthorization()}` },
            });
        },
        {
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

// GET /kanban/cards/:investor_id?investor_status_id=&investor_pipeline_id=&offset=&limit=&name=&...
// -> { data: { cards: [...], count } }  (per-column "load more")
export const FetchKanbanColumnCards = () => {
    return useMutation(
        ({ investor_id, investor_status_id, investor_pipeline_id, offset, limit, name, category, sub_sector, select_stages, city }) => {
            const qs = buildParams({ investor_status_id, investor_pipeline_id, offset, limit, name, category, sub_sector, select_stages, city });
            return axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/cards/${investor_id}${qs}`,
                headers: { Authorization: `Bearer ${checkForAuthorization()}` },
            });
        },
        {
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

// GET /kanban/ids/:investor_id?investor_status_id=&investor_pipeline_id=&name=&...
// -> { data: { items: [{ pipeline_id, startup_id, investor_status_id }], truncated } }
// Resolves a whole selected column at action time; selecting itself never calls the API.
export const FetchKanbanColumnIds = () => {
    return useMutation(
        ({ investor_id, investor_status_id, investor_pipeline_id, name, category, sub_sector, select_stages, city }) => {
            const qs = buildParams({ investor_status_id, investor_pipeline_id, name, category, sub_sector, select_stages, city });
            return axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/ids/${investor_id}${qs}`,
                headers: { Authorization: `Bearer ${checkForAuthorization()}` },
            });
        },
        {
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

// PUT /kanban/move-many  body { pipeline_ids, investor_status_id, status }
// Bulk move for the floating bar: one request for every selected card.
export const MoveKanbanCards = () => {
    return useMutation(
        (payload) => {
            return axios({
                method: "put",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/move-many`,
                headers: {
                    Authorization: `Bearer ${checkForAuthorization()}`,
                    "Content-Type": "application/json",
                },
                data: payload,
            });
        },
        {
            retry: 0,
            onError: (err) => verifyToken(err),
        }
    );
};

// PUT /kanban/move/:pipeline_id  body { investor_status_id, status, display_order? }
// Silent status move (no startup emails).
export const MoveKanbanCard = () => {
    return useMutation(
        ({ pipeline_id, payload }) => {
            return axios({
                method: "put",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/move/${pipeline_id}`,
                headers: {
                    Authorization: `Bearer ${checkForAuthorization()}`,
                    "Content-Type": "application/json",
                },
                data: payload,
            });
        },
        {
            retry: 0,
            onError: (err) => verifyToken(err),
        }
    );
};

// Undo a board move: cancels the held notification (if still waiting) and restores the
// fields the move overwrote. `previous` and `email_job_id` come from the move response.
export const UndoKanbanMove = () => {
    return useMutation(
        ({ pipeline_id, previous, email_job_id }) => {
            return axios({
                method: "put",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/undo/${pipeline_id}`,
                headers: {
                    Authorization: `Bearer ${checkForAuthorization()}`,
                    "Content-Type": "application/json",
                },
                data: { previous, email_job_id },
            });
        },
        {
            retry: 0,
            onError: (err) => verifyToken(err),
        }
    );
};

// Save a manual position: the dropped cards (in drop order) get ranks between their
// neighbours in the target column. Returns each card's previous rank (for Undo).
export const PlaceKanbanCards = () => {
    return useMutation(
        (payload) => {
            return axios({
                method: "put",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/place`,
                headers: {
                    Authorization: `Bearer ${checkForAuthorization()}`,
                    "Content-Type": "application/json",
                },
                data: payload,
            });
        },
        {
            retry: 0,
            onError: (err) => verifyToken(err),
        }
    );
};
